import { useEffect, useMemo, useState } from "react";
import { Check, Gift, X } from "lucide-react";
import { api, gbp, imgSrc } from "@/lib/api";
import { useCart } from "@/context/CartContext";

const TierPicker = ({ tiers, tier, setTier }) => (
  <div className="mt-8 flex flex-wrap gap-3" data-testid="hamper-tiers">
    {tiers.map((t) => (
      <button key={t.size} onClick={() => setTier(t)} className={`rounded-2xl border-2 px-6 py-4 text-left transition-colors ${tier?.size === t.size ? "border-[#F3D9A4] bg-white/15" : "border-white/20 hover:border-white/50"}`} data-testid={`hamper-tier-${t.size}`}>
        <p className="font-serif text-2xl font-bold">{t.size} treats</p>
        <p className="text-[#F3D9A4] font-semibold">{gbp(t.price)}</p>
      </button>
    ))}
  </div>
);

const PickCard = ({ p, picked, full, toggle }) => (
  <button onClick={() => toggle(p)} disabled={!picked && full} className={`relative text-left rounded-2xl border-2 bg-white p-3 transition-colors disabled:opacity-40 ${picked ? "border-[#8C2220]" : "border-[#EDE4D3] hover:border-slate-400"}`} data-testid={`hamper-pick-${p.id}`}>
    {picked && <span className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[#8C2220] text-white grid place-items-center"><Check className="w-4 h-4" /></span>}
    <img src={imgSrc(p.image_url)} alt={p.name} className="w-full aspect-square object-contain" loading="lazy" />
    <p className="mt-2 text-sm font-medium leading-snug line-clamp-2">{p.name}</p>
    <p className="text-xs text-slate-500">{p.category}</p>
  </button>
);

export default function Hampers() {
  const [data, setData] = useState(null);
  const [tier, setTier] = useState(null);
  const [picks, setPicks] = useState([]);
  const [cat, setCat] = useState("All");
  const { addHamper } = useCart();

  useEffect(() => {
    document.title = "Build a Gift Hamper | Albion Pantry";
    api.get("/hampers").then((r) => { setData(r.data); setTier(r.data.tiers[1] || r.data.tiers[0] || null); });
    return () => { document.title = "Albion Pantry | Fine British Food"; };
  }, []);
  useEffect(() => { if (tier && picks.length > tier.size) setPicks(picks.slice(0, tier.size)); }, [tier]); // eslint-disable-line react-hooks/exhaustive-deps

  const cats = useMemo(() => ["All", ...new Set((data?.products || []).map((p) => p.category))], [data]);
  if (!data) return <div className="max-w-7xl mx-auto px-4 py-24 text-slate-500">Loading…</div>;
  if (!tier) return <div className="max-w-7xl mx-auto px-4 py-24 text-slate-500" data-testid="hampers-unavailable">Hampers are coming soon.</div>;

  const full = picks.length >= tier.size;
  const toggle = (p) => setPicks(picks.some((x) => x.id === p.id) ? picks.filter((x) => x.id !== p.id) : full ? picks : [...picks, p]);
  const shown = data.products.filter((p) => cat === "All" || p.category === cat);
  const value = picks.reduce((s, p) => s + p.price, 0);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="hampers-page">
      <div className="rounded-3xl bg-[#1B3626] text-white p-8 sm:p-12 relative overflow-hidden">
        <Gift className="absolute -right-6 -bottom-6 w-48 h-48 text-white/10" />
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#F3D9A4]">Build your own</p>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl lg:text-6xl font-bold max-w-2xl">Gift Hampers</h1>
        <p className="mt-4 max-w-xl text-white/80">Choose a size, pick your favourite chocolates, tins, biscuits and treats, and pay one fixed price. We pack it in a gift box and post it with Evri.</p>
        <TierPicker tiers={data.tiers} tier={tier} setTier={setTier} />
      </div>
      <div className="mt-10 grid lg:grid-cols-[1fr_340px] gap-8 items-start">
        <div>
          <div className="flex flex-wrap gap-2" data-testid="hamper-category-filters">
            {cats.map((c) => <button key={c} onClick={() => setCat(c)} className={`rounded-full px-4 py-2 text-sm border ${cat === c ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white border-[#E2D7C3]"}`} data-testid={`hamper-cat-${c.toLowerCase().replace(/\W+/g, "-")}`}>{c}</button>)}
          </div>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4">
            {shown.map((p) => <PickCard key={p.id} p={p} picked={picks.some((x) => x.id === p.id)} full={full} toggle={toggle} />)}
          </div>
        </div>
        <aside className="lg:sticky lg:top-32 rounded-2xl bg-white border border-[#EDE4D3] p-5" data-testid="hamper-summary">
          <p className="font-serif text-xl font-bold">Your {tier.size}-treat hamper</p>
          <p className="text-sm text-slate-500" data-testid="hamper-progress">{picks.length} of {tier.size} chosen</p>
          <div className="mt-3 h-2 rounded-full bg-[#F7F3EB] overflow-hidden"><div className="h-full bg-[#8C2220] transition-[width]" style={{ width: `${(picks.length / tier.size) * 100}%` }} /></div>
          <ul className="mt-4 space-y-2 text-sm">
            {picks.map((p) => <li key={p.id} className="flex gap-2 items-start"><span className="flex-1">{p.name}</span><button onClick={() => toggle(p)} aria-label="Remove" className="text-slate-400 hover:text-[#8C2220]" data-testid={`hamper-remove-${p.id}`}><X className="w-4 h-4" /></button></li>)}
          </ul>
          <div className="mt-4 border-t border-[#EDE4D3] pt-4 flex justify-between items-baseline">
            <span className="text-sm text-slate-500">{value > tier.price && <>Worth <s>{gbp(value)}</s></>}</span>
            <span className="font-serif text-2xl font-bold" data-testid="hamper-price">{gbp(tier.price)}</span>
          </div>
          <button disabled={!full} onClick={() => { addHamper(tier, picks); setPicks([]); }} className="mt-4 w-full rounded-full bg-[#8C2220] text-white py-3 font-semibold disabled:opacity-40" data-testid="hamper-add-to-basket">
            {full ? "Add hamper to basket" : `Choose ${tier.size - picks.length} more`}
          </button>
          <p className="mt-3 text-xs text-slate-500">Add a gift message at checkout.</p>
        </aside>
      </div>
    </div>
  );
}
